import { useEffect, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import { useAgentPortalGuard } from "./useAgentPortalGuard.js";
import AgentPortalGuardState from "./AgentPortalGuardState.jsx";
import { useFootstepHistory } from "../footsteps/useFootstepHistory.js";
import { listWorkerProfiles } from "../workerProfiles/workerProfilesApi.js";
import "./agentFootsteps.css";
import "../pages/states.css";

const CRUMBS = ["Backoffice", "Agent", "Footstep"];
const SEARCH_DEBOUNCE_MS = 350;
const MAX_POINTS_CEILING = 2000;

const PRIVACY_BANNER_TITLE = "ข้อมูล Footstep เป็นข้อมูลอ่อนไหว";
const PRIVACY_BANNER_LINE_1 = "ต้องระบุเหตุผลก่อนเปิดดูประวัติ";
const PRIVACY_BANNER_LINE_2 = "ระบบจะบันทึกผู้เปิดดู ช่วงเวลา และจำนวนจุด แต่ไม่บันทึกรายการพิกัดทั้งหมดใน audit log";

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function describeError(error) {
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูประวัติ Footstep ของแรงงานรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบข้อมูลแรงงานรายนี้";
  }
  return "ไม่สามารถโหลดประวัติ Footstep ได้ กรุณาลองใหม่อีกครั้ง";
}

function validateForm({ workerId, purpose, fromUtc, toUtc, maxPoints }) {
  const errors = [];
  if (!workerId) {
    errors.push("กรุณาเลือกแรงงานก่อนเปิดดู Footstep");
  }
  if (!fromUtc || !toUtc) {
    errors.push("กรุณาระบุช่วงเวลา From (UTC) และ To (UTC) ให้ครบถ้วน");
  } else if (new Date(fromUtc).getTime() >= new Date(toUtc).getTime()) {
    errors.push("From (UTC) ต้องอยู่ก่อน To (UTC)");
  }
  if (!purpose.trim()) {
    errors.push("กรุณาระบุเหตุผลในการเปิดดู (Purpose) ก่อนเปิดดู");
  }
  if (maxPoints !== "" && maxPoints !== null) {
    const numeric = Number(maxPoints);
    if (!Number.isInteger(numeric) || numeric < 1 || numeric > MAX_POINTS_CEILING) {
      errors.push(`Max Points ต้องเป็นจำนวนเต็มระหว่าง 1 ถึง ${MAX_POINTS_CEILING}`);
    }
  }
  return errors;
}

/**
 * /backoffice/agent/footsteps - Prompt 138's Agent Portal Footstep History
 * screen. Reuses the real Prompt 114 Footstep History API
 * (../footsteps/footstepsApi.js's getFootstepHistory/getFootstepSummary,
 * via ../footsteps/useFootstepHistory.js) completely unchanged, including
 * its purpose-required privacy gate (FootstepService.GetHistoryAsync
 * rejects a missing/blank purpose with a 400) - never weakened for the
 * Agent Portal. The worker picker searches the same real, Agent-scoped
 * Worker Profile list this portal's own Workers page uses
 * (../workerProfiles/workerProfilesApi.js), so an Agent can only ever pick
 * one of their own real workers, never type an arbitrary id. The
 * SuperAdmin-only Access Logs (compliance) tab from
 * ../footsteps/FootstepHistoryPage.jsx is deliberately not reproduced here
 * (BackofficeFootstepController.ListAccessLogs 403s for any non-SuperAdmin
 * caller) - offering a control that would only ever fail is worse than not
 * offering it.
 */
export default function AgentFootstepsPage() {
  const guard = useAgentPortalGuard();
  const { historyState, summaryState, runHistoryQuery, runSummaryQuery, resetSummary } = useFootstepHistory();

  const [workerQuery, setWorkerQuery] = useState("");
  const [workerSearch, setWorkerSearch] = useState("");
  const [workerResults, setWorkerResults] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [purpose, setPurpose] = useState("");
  const [fromUtc, setFromUtc] = useState("");
  const [toUtc, setToUtc] = useState("");
  const [maxPoints, setMaxPoints] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setWorkerSearch(workerQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [workerQuery]);

  useEffect(() => {
    if (guard.status !== "ready" || !workerSearch.trim()) {
      setWorkerResults([]);
      return undefined;
    }
    let cancelled = false;
    listWorkerProfiles({ search: workerSearch, page: 1, pageSize: 8 })
      .then((data) => {
        if (!cancelled) {
          setWorkerResults(data?.items ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWorkerResults([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [guard.status, workerSearch]);

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={CRUMBS} guard={guard} />;
  }

  function pickWorker(item) {
    setSelectedWorker({ workerId: item.workerId, name: item.worker.fullNameNative || item.worker.fullNameEnglish || item.workerCode });
    setWorkerResults([]);
    setWorkerQuery("");
  }

  function handleSubmit(event) {
    event.preventDefault();
    const errors = validateForm({ workerId: selectedWorker?.workerId, purpose, fromUtc, toUtc, maxPoints });
    setValidationErrors(errors);
    if (errors.length > 0) {
      return;
    }
    runHistoryQuery(selectedWorker.workerId, {
      fromUtc: new Date(fromUtc).toISOString(),
      toUtc: new Date(toUtc).toISOString(),
      purpose: purpose.trim(),
      maxPoints: maxPoints ? Number(maxPoints) : null,
    }).catch(() => {});
  }

  function handleOpenSummary() {
    if (!selectedWorker) {
      return;
    }
    runSummaryQuery(selectedWorker.workerId, { fromUtc: new Date(fromUtc).toISOString(), toUtc: new Date(toUtc).toISOString() }).catch(() => {});
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-afs-page">
        <header className="wx-afs-head">
          <div>
            <h1>Footstep ของสายงานฉัน</h1>
            <p>ดูประวัติเส้นทางของแรงงานในสายงานคุณ ต้องระบุเหตุผลทุกครั้งก่อนเปิดดู</p>
          </div>
        </header>

        <div className="wx-afs-privacy-banner" role="note">
          <div className="wx-afs-privacy-title">
            <span className="wx-afs-privacy-dot" aria-hidden="true" />
            {PRIVACY_BANNER_TITLE}
          </div>
          <ul>
            <li>{PRIVACY_BANNER_LINE_1}</li>
            <li>{PRIVACY_BANNER_LINE_2}</li>
          </ul>
        </div>

        <section className="wx-panel wx-afs-query-panel">
          <h2 className="wx-panel-title">ค้นหาประวัติ Footstep</h2>
          <form onSubmit={handleSubmit}>
            <div className="wx-afs-form-grid">
              <label className="wx-afs-field wx-afs-field--span-2">
                <span>
                  แรงงาน <b>*</b>
                </span>
                {selectedWorker ? (
                  <div className="wx-afs-worker-chip">
                    <strong>{selectedWorker.name}</strong>
                    <button type="button" className="wx-button wx-button--ghost" onClick={() => setSelectedWorker(null)}>
                      เปลี่ยน
                    </button>
                  </div>
                ) : (
                  <div className="wx-afs-worker-picker">
                    <input
                      type="text"
                      placeholder="พิมพ์ชื่อหรือรหัสแรงงาน..."
                      aria-label="ค้นหาแรงงาน"
                      value={workerQuery}
                      onChange={(event) => setWorkerQuery(event.target.value)}
                    />
                    {workerResults.length > 0 && (
                      <ul className="wx-afs-worker-results">
                        {workerResults.map((item) => (
                          <li key={item.workerId}>
                            <button type="button" onClick={() => pickWorker(item)}>
                              <strong>{item.worker.fullNameNative || item.worker.fullNameEnglish || "-"}</strong>
                              <span>{item.workerCode}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </label>

              <label className="wx-afs-field">
                <span>
                  From (UTC) <b>*</b>
                </span>
                <input type="datetime-local" value={fromUtc} onChange={(event) => setFromUtc(event.target.value)} />
              </label>
              <label className="wx-afs-field">
                <span>
                  To (UTC) <b>*</b>
                </span>
                <input type="datetime-local" value={toUtc} onChange={(event) => setToUtc(event.target.value)} />
              </label>
              <label className="wx-afs-field">
                <span>Max Points (ไม่บังคับ)</span>
                <input type="number" min="1" max={MAX_POINTS_CEILING} value={maxPoints} onChange={(event) => setMaxPoints(event.target.value)} />
              </label>

              <label className="wx-afs-field wx-afs-field--span-2">
                <span>
                  เหตุผลในการเปิดดู (Purpose) <b>*</b>
                </span>
                <textarea
                  placeholder="เช่น ตรวจสอบเส้นทางระหว่างกะทำงาน"
                  aria-label="เหตุผลในการเปิดดู (Purpose)"
                  value={purpose}
                  onChange={(event) => setPurpose(event.target.value)}
                />
              </label>
            </div>

            {validationErrors.length > 0 && (
              <div className="wx-afs-form-message" role="alert">
                {validationErrors.map((error) => (
                  <p key={error}>{error}</p>
                ))}
              </div>
            )}

            <div className="wx-afs-form-actions">
              <button type="submit" className="wx-button wx-button--primary" disabled={historyState.status === "loading"}>
                {historyState.status === "loading" ? "กำลังโหลด..." : "เปิดดูประวัติ"}
              </button>
            </div>
          </form>
        </section>

        {historyState.status === "error" && (
          <div className="wx-state-card wx-state-card--danger" role="alert">
            <p className="wx-state-body">{describeError(historyState.error)}</p>
          </div>
        )}

        {historyState.status === "ready" && historyState.data && (
          <section className="wx-panel wx-afs-result-panel">
            <div className="wx-afs-result-head">
              <h2 className="wx-panel-title">ผลลัพธ์: {historyState.data.workerName ?? historyState.data.workerCode}</h2>
              <div className="wx-afs-result-badges">
                <span className="wx-badge wx-badge--info">{historyState.data.pointCount} จุด</span>
                <span className="wx-badge wx-badge--neutral">accessLogged: {String(historyState.data.accessLogged)}</span>
                <button type="button" className="wx-button wx-button--ghost" onClick={handleOpenSummary}>
                  เปิดสรุป
                </button>
              </div>
            </div>

            {historyState.data.points.length === 0 ? (
              <p className="wx-empty-list">ไม่พบข้อมูล Footstep ในช่วงเวลานี้</p>
            ) : (
              <div className="wx-afs-table-wrap">
                <table className="wx-afs-table">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>Latitude</th>
                      <th>Longitude</th>
                      <th>ความแม่นยำ (m)</th>
                      <th>Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyState.data.points.map((point) => (
                      <tr key={point.locationEventId}>
                        <td>{formatDateTimeTh(point.capturedAtUtc) ?? "-"}</td>
                        <td>{point.latitude}</td>
                        <td>{point.longitude}</td>
                        <td>{point.accuracyMeters ?? "-"}</td>
                        <td>
                          {point.provider ?? "-"}
                          {point.isMockProvider ? " (mock)" : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {summaryState.status === "ready" && summaryState.data && (
              <div className="wx-afs-summary-box">
                <h3>สรุปช่วงเวลา</h3>
                <div className="wx-afs-summary-grid">
                  <div>
                    <span>จุดทั้งหมด</span>
                    <strong>{summaryState.data.totalPoints}</strong>
                  </div>
                  <div>
                    <span>จุดแรก</span>
                    <strong>{formatDateTimeTh(summaryState.data.firstPointAtUtc) ?? "-"}</strong>
                  </div>
                  <div>
                    <span>จุดล่าสุด</span>
                    <strong>{formatDateTimeTh(summaryState.data.lastPointAtUtc) ?? "-"}</strong>
                  </div>
                  <div>
                    <span>Mock provider</span>
                    <strong>{summaryState.data.mockProviderPointCount}</strong>
                  </div>
                </div>
                <button type="button" className="wx-button wx-button--ghost" onClick={resetSummary}>
                  ปิดสรุป
                </button>
              </div>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
